/* ==========================================================================
   <recently-viewed>
   Two jobs, both client-side (first-party functional storage, no consent gate):
     1. Track  — push the current PDP's handle onto a localStorage list.
     2. Render — pull the list, drop the current product, fetch a server-rendered
                 card per handle via the Section Rendering API, show the section.
   Degrades silently: no handles / no storage / fetch fail → stays hidden.
   ========================================================================== */

const RV_KEY = 'ev_recently_viewed';
const RV_CAP = 12;

class RecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.current = this.dataset.handle || '';
    this.limit = parseInt(this.dataset.limit || '4', 10);
    this.cardSection = this.dataset.cardSection || 'recently-viewed-card';
    this.list = this.querySelector('[data-rv-list]');
    this._renderVersion = 0;
    this._onColorChange = this._onColorChange || ((event) => {
      const handle = event.detail?.handle;
      if (!handle || handle === this.current) return;
      this.current = handle;
      this.dataset.handle = handle;
      this._track();
      this.list?.replaceChildren();
      this.hidden = true;
      this._render();
    });
    document.addEventListener('pdp:color-change', this._onColorChange);

    this._track();
    this._render();
  }

  disconnectedCallback() {
    document.removeEventListener('pdp:color-change', this._onColorChange);
    this._renderVersion += 1;
  }

  _read() {
    try { return JSON.parse(localStorage.getItem(RV_KEY)) || []; }
    catch (_) { return []; }
  }

  _write(arr) {
    try { localStorage.setItem(RV_KEY, JSON.stringify(arr)); }
    catch (_) { /* private mode / quota — non-fatal */ }
  }

  /* Most-recent-first, deduped, current product on top, capped. */
  _track() {
    if (!this.current) return;
    const next = [this.current, ...this._read().filter((h) => h !== this.current)].slice(0, RV_CAP);
    this._write(next);
  }

  async _render() {
    const version = ++this._renderVersion;
    if (!this.list) return;
    const handles = this._read().filter((h) => h !== this.current).slice(0, this.limit);
    if (!handles.length) return; // host stays hidden

    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const cards = await Promise.all(handles.map((h) => this._fetchCard(root, h)));
    if (version !== this._renderVersion) return;

    const frag = document.createDocumentFragment();
    cards.forEach((card) => {
      if (!card) return;
      const item = document.createElement('div');
      item.className = 'rv__item';
      item.appendChild(card);
      frag.appendChild(item);
    });

    if (!frag.childNodes.length) return;
    this.list.appendChild(frag);
    this.hidden = false;

    // These cards were injected after theme.js's initial hover-image pass, so
    // their deferred secondary images still carry data-src with no src. Wire
    // them now, or the hover swap reveals a blank frame instead of image #2.
    if (window.EssentialsVault && window.EssentialsVault.wireHoverImages) {
      window.EssentialsVault.wireHoverImages(this.list);
    }
  }

  async _fetchCard(root, handle) {
    try {
      const res = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=${this.cardSection}`);
      if (!res.ok) return null;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector('.card-product'); // lift the card, drop the section wrapper
    } catch (_) {
      return null;
    }
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewed);
}
